import { useCallback, useEffect, useRef, useState } from 'react';
import { usePostValidatePixQrCodeKey } from '@ibk/pagamentos';
import { useRechargePaymentCode } from '../../../../../hooks';
import { RechargePaymentMethod } from '../../../../../types';
const MAX_RETRY_TIME_MS = 10000;
const RETRY_DELAY_MS = 0;
export function usePaymentCodeWithRetry({ params, paymentMethod, onSuccess, onError, }) {
    const [isRetrying, setIsRetrying] = useState(false);
    const [retryError, setRetryError] = useState();
    const timeoutRef = useRef(null);
    const retryTimeoutRef = useRef(null);
    const shouldValidatePix = paymentMethod === RechargePaymentMethod.PIX || paymentMethod === RechargePaymentMethod.ACCOUNT_BALANCE;
    const isBankSlip = paymentMethod === RechargePaymentMethod.BANK_SLIP;
    const getPaymentCode = useCallback((code) => {
        if (!code)
            return null;
        if (isBankSlip) {
            return code.bankSlipSerialNumber ?? null;
        }
        return code.copyAndPaste ?? null;
    }, [isBankSlip]);
    const { paymentCode, isLoading: isPaymentCodeLoading, isError: isPaymentCodeError, fetchPaymentCode, } = useRechargePaymentCode();
    const { qrCodePixKey, error: errorValidatePixQrCodeKey, postValidateQrCodePixKey, loading: isValidatePixQrCodeKeyLoading, setQrCodePixKey, setError: setErrorValidatePixQrCodeKey, } = usePostValidatePixQrCodeKey();
    const stopRetry = useCallback(() => {
        setIsRetrying(false);
        setRetryError(undefined);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, []);
    const startRetry = useCallback(() => {
        if (!params)
            return;
        const code = getPaymentCode(paymentCode);
        const isSuccess = code && (!shouldValidatePix || qrCodePixKey);
        if (isSuccess && code) {
            onSuccess?.(code);
            return;
        }
        setRetryError(undefined);
        timeoutRef.current = setTimeout(() => {
            const errorMessage = 'Tempo limite excedido ao obter código de pagamento';
            setRetryError(errorMessage);
            stopRetry();
            onError?.(errorMessage);
        }, MAX_RETRY_TIME_MS);
        setIsRetrying(true);
    }, [params, paymentCode, qrCodePixKey, shouldValidatePix, stopRetry, onError, onSuccess, getPaymentCode]);
    useEffect(() => {
        if (!isRetrying || !params)
            return;
        const code = getPaymentCode(paymentCode);
        if (code) {
            if (shouldValidatePix && paymentCode?.copyAndPaste && !qrCodePixKey && !isValidatePixQrCodeKeyLoading) {
                retryTimeoutRef.current = setTimeout(async () => {
                    try {
                        await postValidateQrCodePixKey(paymentCode.copyAndPaste, true);
                    }
                    catch {
                    }
                }, RETRY_DELAY_MS);
                return;
            }
            const isReady = !shouldValidatePix || (qrCodePixKey && !isPaymentCodeLoading && !isValidatePixQrCodeKeyLoading);
            if (isReady && !isPaymentCodeLoading) {
                stopRetry();
                onSuccess?.(code);
            }
            return;
        }
        if (!isPaymentCodeLoading) {
            retryTimeoutRef.current = setTimeout(async () => {
                const currentCode = getPaymentCode(paymentCode);
                if (!currentCode) {
                    try {
                        const result = await fetchPaymentCode(params);
                        if (result?.copyAndPaste && shouldValidatePix) {
                            try {
                                await postValidateQrCodePixKey(result.copyAndPaste, true);
                            }
                            catch {
                            }
                        }
                    }
                    catch {
                    }
                }
            }, RETRY_DELAY_MS);
        }
    }, [
        isRetrying,
        params,
        isPaymentCodeError,
        isPaymentCodeLoading,
        isValidatePixQrCodeKeyLoading,
        shouldValidatePix,
        fetchPaymentCode,
        stopRetry,
        onSuccess,
        paymentCode,
        qrCodePixKey,
        getPaymentCode,
        postValidateQrCodePixKey,
    ]);
    useEffect(() => {
        return () => {
            stopRetry();
        };
    }, [stopRetry]);
    useEffect(() => {
        if (errorValidatePixQrCodeKey && isRetrying) {
            onError?.(errorValidatePixQrCodeKey);
        }
    }, [errorValidatePixQrCodeKey, isRetrying, onError]);
    useEffect(() => {
        if (params?.voucherContractId && params?.voucherContractRechargeId) {
            setQrCodePixKey(undefined);
            setErrorValidatePixQrCodeKey(undefined);
        }
    }, [params?.voucherContractId, params?.voucherContractRechargeId, setQrCodePixKey, setErrorValidatePixQrCodeKey]);
    const error = retryError || errorValidatePixQrCodeKey;
    return {
        startRetry,
        stopRetry,
        isRetrying,
        error,
    };
}
